import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/state/store/auth';
import {
  createRole,
  deleteRole,
  getOrganizationRoleRows,
  getRolePermissions,
  listRoleUsers,
  setRolePermissions,
  updateRole,
} from '../services/role.service';

const ROLES_QUERY_KEY = 'hr-organization-roles';
const ROLE_PERMISSIONS_QUERY_KEY = 'hr-role-permissions';
const ROLE_USERS_QUERY_KEY = 'hr-role-users';

/** After a role change: the Role Manager rows, and the role names the directory dialog shows. */
const invalidateRoleQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: [ROLES_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: ['iam', 'roles'] });
};

const useOrganizationId = () => useAuthStore((state) => state.selectedOrgId) ?? '';

export const useOrganizationRoles = () => {
  const organizationId = useOrganizationId();
  return useGlobalQuery({
    queryKey: [ROLES_QUERY_KEY, organizationId],
    queryFn: getOrganizationRoleRows,
    enabled: Boolean(organizationId),
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: async (input: { slug: string; name: string; description: string }) => {
      try {
        return await createRole(input);
      } catch (error) {
        if ((error as Error).message !== 'ROLE_KEY_ARCHIVED') throw error;
        toast({ variant: 'destructive', title: t('ROLE_KEY_ARCHIVED') });
        return 'archived' as const;
      }
    },
    onSuccess: (result) => {
      if (result === 'archived') return;
      if (result === 'existing') {
        toast({ variant: 'destructive', title: t('ROLE_KEY_EXISTS') });
        return;
      }
      invalidateRoleQueries(queryClient);
      toast({ title: t('ROLE_CREATED') });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: ({ slug, input }: { slug: string; input: { name: string; description: string } }) =>
      updateRole(slug, input),
    onSuccess: () => {
      invalidateRoleQueries(queryClient);
      toast({ title: t('ROLE_UPDATED') });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (slug: string) => deleteRole(slug),
    onSuccess: (result) => {
      if (!result.deleted) {
        toast({
          variant: 'destructive',
          title: t('ROLE_HAS_USERS', { count: result.userCount }),
        });
        return;
      }
      invalidateRoleQueries(queryClient);
      toast({ title: t('ROLE_DELETED') });
    },
  });
};

/** What a role grants in the current organization (permission resources). */
export const useRolePermissions = (slug: string | null) => {
  const organizationId = useOrganizationId();
  return useGlobalQuery({
    queryKey: [ROLE_PERMISSIONS_QUERY_KEY, organizationId, slug],
    queryFn: () => getRolePermissions(slug ?? '', organizationId),
    enabled: Boolean(slug && organizationId),
  });
};

/** The users who hold a role in the current organization. */
export const useRoleUsers = (slug: string | null) => {
  const organizationId = useOrganizationId();
  return useGlobalQuery({
    queryKey: [ROLE_USERS_QUERY_KEY, organizationId, slug],
    queryFn: () => listRoleUsers(slug ?? '', organizationId),
    enabled: Boolean(slug && organizationId),
  });
};

export const useSaveRolePermissions = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();
  const organizationId = useOrganizationId();

  return useGlobalMutation({
    mutationFn: ({ slug, resources }: { slug: string; resources: string[] }) =>
      setRolePermissions(slug, organizationId, resources),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROLE_PERMISSIONS_QUERY_KEY] });
      toast({ title: t('ROLE_PERMISSIONS_SAVED') });
    },
  });
};
